import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import Button from 'kingpin/atoms/Button';

import { NoNumberRows } from './NumberRowFontSizeContext';

// Relative: a bare 'constants' resolves to Node's built-in module
import { CANVAS_VERTICAL_SNAPPING_POINTS, Z_INDEX } from '../../constants';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${Z_INDEX.STUDIO_FULLSCREEN};
  display: flex;
  background-color: var(--color-bg-secondary);
`;

const Body = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding: 48px;
`;

const Controls = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
`;

// Kingpin buttons take no title, so hover hints go on a wrapper
export const Hint = styled.span`
  display: flex;
`;

// A block drawn fullscreen takes a full screen of the grid (every column, a
// screen of rows), so charts lay out as they would at that size
export const FULLSCREEN_LAYOUT = {
  x: 0,
  y: 0,
  w: 12,
  h: CANVAS_VERTICAL_SNAPPING_POINTS,
};

// Headline numbers scale with the window, like they do with a block's size
export const getFullscreenValueFontSize = () =>
  Math.round(
    Math.max(32, Math.min(window.innerHeight * 0.2, window.innerWidth * 0.1)),
  );

const isTyping = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  return (
    !!el && (['INPUT', 'TEXTAREA'].includes(el.tagName) || el.isContentEditable)
  );
};

// A block shown over the whole window, view-only. Escape (captured, so
// nothing underneath also handles it) or × closes it, unless Escape is
// pressed while typing. Portalled so a canvas's overflow and stacking can't
// clip it; context (dates, filters, metrics) still reaches through the
// portal.
const FullscreenOverlay = ({
  children,
  onClose,
  controls,
  aside,
}: {
  children: React.ReactNode;
  onClose: () => void;
  // Buttons before the close button
  controls?: React.ReactNode;
  // Beside the block, e.g. Studio's settings column
  aside?: React.ReactNode;
}) => {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isTyping(e.target)) {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () =>
      window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [onClose]);

  return createPortal(
    <Overlay
      role="dialog"
      aria-label="Fullscreen gadget"
      data-testid="studio-fullscreen"
    >
      <Body>
        <NoNumberRows>{children}</NoNumberRows>
        <Controls>
          {controls}
          <Hint title="Close fullscreen">
            <Button
              type="Secondary"
              size="Small"
              icon="cross"
              onClick={onClose}
              testId="studio-fullscreen-close"
            />
          </Hint>
        </Controls>
      </Body>
      {aside}
    </Overlay>,
    document.body,
  );
};

export default FullscreenOverlay;
